import { apiFetch, parseJsonSafe } from './client';
import { buildSessionLengthPayload } from '../utils/sessionLength';

export const MASTERY_SELECTION_REASONS = [
  'active_attempt',
  'blocked_by_prereqs',
  'progress_next',
  'review_due',
  'weak_resurface',
];

export function feedbackKindForCategory(category) {
  const kindByCategory = {
    bug: 'bug',
    confusing: 'confusing',
    idea: 'idea',
    praise: 'praise',
    feature_request: 'idea',
    usability_confusion: 'confusing',
    content_quality: 'confusing',
    marking_issue: 'bug',
    generation_issue: 'bug',
    mastery_issue: 'bug',
  };
  return kindByCategory[category] || 'bug';
}

function normalizedReasonTokens(selection) {
  if (!selection || typeof selection !== 'object') {
    return null;
  }
  const reason = String(selection?.reason || '').trim();
  const detail = String(selection?.reason_detail || '').trim();
  const flags = Array.isArray(selection?.reason_flags)
    ? selection.reason_flags.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  return { reason, detail, flags };
}

export function describeSelectionReason(selection) {
  const tokens = normalizedReasonTokens(selection);
  if (!tokens) {
    return null;
  }
  const { reason, detail, flags } = tokens;
  if (!reason && !detail && !flags.length) {
    return null;
  }

  if (reason === 'active_attempt') {
    return 'Continuing the current question because the student already has an active attempt in progress.';
  }
  if (reason === 'blocked_by_prereqs' || detail === 'prerequisite_detour') {
    return 'Chosen to reinforce a prerequisite skill needed before the current topic can continue.';
  }
  if (
    reason === 'weak_resurface'
    || detail === 'micro_review'
    || flags.includes('micro_review')
    || detail === 'sticky_micro'
    || flags.includes('sticky_micro')
  ) {
    return 'Chosen to revisit a weak area that still needs more evidence.';
  }
  if (reason === 'review_due' || detail === 'overdue_review') {
    return 'Chosen for review because this skill is due to be checked again.';
  }
  if (reason === 'progress_next') {
    return 'Chosen as the next progression step after recent success.';
  }
  return null;
}

export function describeLearningStatus(selection) {
  const tokens = normalizedReasonTokens(selection);
  if (!tokens) {
    return null;
  }
  const { reason, detail, flags } = tokens;
  if (!reason && !detail && !flags.length) {
    return null;
  }

  if (reason === 'active_attempt') {
    return 'Continuing current work';
  }
  if (reason === 'blocked_by_prereqs' || detail === 'prerequisite_detour') {
    return 'Refreshing a prerequisite';
  }
  if (
    reason === 'weak_resurface'
    || detail === 'micro_review'
    || flags.includes('micro_review')
    || detail === 'sticky_micro'
    || flags.includes('sticky_micro')
  ) {
    return 'Revisiting a weak area';
  }
  if (reason === 'review_due' || detail === 'overdue_review') {
    return 'Reviewing a previously learned skill';
  }
  if (reason === 'progress_next') {
    return 'Building forward progress';
  }
  return null;
}

export function describeSuccessCriteria(selection) {
  const tokens = normalizedReasonTokens(selection);
  if (!tokens) {
    return null;
  }
  const { reason, detail, flags } = tokens;
  if (!reason && !detail && !flags.length) {
    return null;
  }

  if (reason === 'active_attempt') {
    return 'Finish this question clearly enough to show what the student can do on this skill.';
  }
  if (reason === 'blocked_by_prereqs' || detail === 'prerequisite_detour') {
    return 'Show that the prerequisite method is secure enough to return to the main skill.';
  }
  if (
    reason === 'weak_resurface'
    || detail === 'micro_review'
    || flags.includes('micro_review')
    || detail === 'sticky_micro'
    || flags.includes('sticky_micro')
  ) {
    return 'Show a secure method here so the session can judge whether this weak area is improving.';
  }
  if (reason === 'review_due' || detail === 'overdue_review') {
    return 'Show that this skill is still secure when it comes back for review.';
  }
  if (reason === 'progress_next') {
    return 'Show the method is secure enough to keep moving forward on this skill.';
  }
  return null;
}

export function describeNextStepHint(selection) {
  const tokens = normalizedReasonTokens(selection);
  if (!tokens) {
    return null;
  }
  const { reason, detail, flags } = tokens;
  if (!reason && !detail && !flags.length) {
    return null;
  }

  if (reason === 'active_attempt') {
    return 'After this is finished, the session will choose the best follow-up.';
  }
  if (reason === 'blocked_by_prereqs' || detail === 'prerequisite_detour') {
    return 'If this looks secure, the session will likely return to the harder skill it was preparing for.';
  }
  if (
    reason === 'weak_resurface'
    || detail === 'micro_review'
    || flags.includes('micro_review')
    || detail === 'sticky_micro'
    || flags.includes('sticky_micro')
  ) {
    return 'If this goes well, the session will likely keep checking this skill or return to the next priority.';
  }
  if (reason === 'review_due' || detail === 'overdue_review') {
    return 'If this stays secure, the session will likely move back into forward progress.';
  }
  if (reason === 'progress_next') {
    return 'If this goes well, the session will likely continue forward on this skill.';
  }
  return null;
}

/**
 * Known backend-facing reasons. Other non-empty strings are preserved so the frontend
 * does not silently collapse newer runtime reasons.
 * @typedef {string} MasterySelectionReason
 */

/**
 * @typedef {Object} MasterySelection
 * @property {MasterySelectionReason} reason
 * @property {string=} reason_detail
 * @property {string[]=} reason_flags
 * @property {string=} family_key
 * @property {string=} macro_skill_code
 * @property {string=} micro_skill_code
 * @property {number=} cognitive_level
 * @property {number=} realism_level
 * @property {string=} progression_family
 */

/**
 * Normalize mastery selection payload to the current API contract.
 * @param {any} selection
 * @returns {(MasterySelection & Record<string, any>) | null}
 */
export function normalizeMasterySelection(selection) {
  if (!selection || typeof selection !== 'object') {
    return null;
  }
  const rawReason = String(selection.reason || '').trim();
  const reason = rawReason || 'progress_next';
  const reasonDetail =
    typeof selection.reason_detail === 'string' && selection.reason_detail.trim()
      ? selection.reason_detail
      : undefined;
  const reasonFlags = Array.isArray(selection.reason_flags)
    ? selection.reason_flags.filter((item) => typeof item === 'string' && item.trim())
    : undefined;

  return {
    ...selection,
    reason,
    ...(reasonDetail ? { reason_detail: reasonDetail } : {}),
    ...(reasonFlags ? { reason_flags: reasonFlags } : {}),
  };
}

export const ACTIVE_SESSION_EXISTS = 'ACTIVE_SESSION_EXISTS';

/**
 * The machine-readable object inside an error response, whether the server sent
 * it at the top level or nested under FastAPI's `detail`.
 */
export function extractStructuredErrorBody(data) {
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    if (data.detail && typeof data.detail === 'object' && !Array.isArray(data.detail)) {
      return data.detail;
    }
    if (typeof data.code === 'string') {
      return data;
    }
  }
  return null;
}

/**
 * Read an ACTIVE_SESSION_EXISTS 409 into the two things the UI needs to offer a
 * choice: which session already exists, and which runtime it belongs to.
 * Returns null for anything else — never guess a conflict.
 */
export function readActiveSessionConflict(data) {
  const structured = extractStructuredErrorBody(data);
  if (!structured || structured.code !== ACTIVE_SESSION_EXISTS) {
    return null;
  }
  const sessionId = structured.active_session_id;
  if (sessionId === undefined || sessionId === null || sessionId === '') {
    return null;
  }
  return {
    sessionId,
    mode: structured.active_session_mode || 'TUTOR',
    startedAt: structured.active_session_started_at || null,
    message: typeof structured.message === 'string' ? structured.message : '',
  };
}

export async function getSessions(filters = {}) {
  const params = new URLSearchParams();
  if (filters.studentId !== undefined && filters.studentId !== null && filters.studentId !== '') {
    params.set('student_id', String(filters.studentId));
  }
  if (filters.tutorUserId !== undefined && filters.tutorUserId !== null && filters.tutorUserId !== '') {
    params.set('tutor_user_id', String(filters.tutorUserId));
  }
  const query = params.toString();
  return apiFetch(query ? `/sessions?${query}` : '/sessions');
}

export async function getSession(sessionId) {
  return apiFetch(`/sessions/${sessionId}`);
}

/**
 * Server-side session runtime state, including `active_attempt_id`.
 *
 * This is the only durable record of "which question is this student part-way
 * through". sessionStorage is per-tab and dies with the tab, so a resume after
 * a closed tab/browser must read the open attempt from here
 * (docs/01_product/session_lifecycle.md:577-581).
 */
export async function getSessionState(sessionId) {
  return apiFetch(`/sessions/${sessionId}/state`);
}

/** An attempt plus its question — used to resume an open attempt without creating one. */
export async function getAttempt(attemptId) {
  return apiFetch(`/attempts/${attemptId}`);
}

export async function postCreateSession(payload) {
  return apiFetch('/sessions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

function normalizeTopicTargets(topicTargets) {
  if (!Array.isArray(topicTargets)) {
    return [];
  }
  return topicTargets
    .map((target) => {
      if (!target || typeof target !== 'object') {
        return null;
      }
      const skillCode = typeof target.skillCode === 'string'
        ? target.skillCode.trim()
        : typeof target.skill_code === 'string'
          ? target.skill_code.trim()
          : '';
      const microSkillCode = typeof target.microSkillCode === 'string'
        ? target.microSkillCode.trim()
        : typeof target.micro_skill_code === 'string'
          ? target.micro_skill_code.trim()
          : '';
      if (!skillCode) {
        return null;
      }
      return {
        skill_code: skillCode,
        ...(microSkillCode ? { micro_skill_code: microSkillCode } : {}),
      };
    })
    .filter(Boolean);
}

export function buildPracticeSessionPayload(options = {}) {
  let selectionMode = 'single_topic';
  if (typeof options.selectionMode === 'string' && options.selectionMode.trim()) {
    selectionMode = options.selectionMode.trim();
  } else if (
    (typeof options.skillCode === 'string' && options.skillCode.trim())
    || (typeof options.microSkillCode === 'string' && options.microSkillCode.trim())
  ) {
    selectionMode = 'single_topic';
  } else if (typeof options.unit === 'string' && options.unit.trim()) {
    selectionMode = 'unit_mix';
  }
  const payload = {
    exam_mode: false,
    track: 'DOUBLE_AWARD_2025',
    tier: typeof options.tier === 'string' && options.tier.trim() ? options.tier.trim() : 'FOUNDATION',
    selection_mode: selectionMode,
    ...buildSessionLengthPayload(options),
  };

  if (selectionMode === 'single_topic') {
    if (typeof options.skillCode === 'string' && options.skillCode.trim()) {
      payload.skill_code = options.skillCode.trim();
    }
    if (typeof options.microSkillCode === 'string' && options.microSkillCode.trim()) {
      payload.micro_skill_code = options.microSkillCode.trim();
    }
  } else if (selectionMode === 'topic_group') {
    const topicGroupCode = typeof options.topicGroupCode === 'string'
      ? options.topicGroupCode.trim()
      : '';
    if (topicGroupCode) {
      payload.topic_group_codes = [topicGroupCode];
    }
  } else if (selectionMode === 'multi_topic') {
    payload.topic_targets = normalizeTopicTargets(options.topicTargets);
  } else if (selectionMode === 'unit_mix') {
    if (typeof options.unit === 'string' && options.unit.trim()) {
      payload.unit = options.unit.trim();
    }
  }

  if (
    selectionMode !== 'unit_mix'
    && typeof options.unit === 'string'
    && options.unit.trim()
    && options.allowOptionalUnit === true
  ) {
    payload.unit = options.unit.trim();
  }

  return payload;
}

export async function postCreateStudentPracticeSession(options = {}) {
  const payload = buildPracticeSessionPayload(options);
  if (!payload.selection_mode && !payload.unit) {
    payload.unit = 'U1';
  }
  if (payload.selection_mode === 'single_topic' && !payload.skill_code && !payload.micro_skill_code) {
    delete payload.selection_mode;
    payload.unit = 'U1';
  }
  const response = await apiFetch('/sessions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await parseJsonSafe(response);

  if (!response.ok) {
    // A structured error body (e.g. the ACTIVE_SESSION_EXISTS 409) is the whole
    // point of the response: the caller must be able to read active_session_id
    // and active_session_mode without a second lookup. FastAPI nests the body
    // under `detail`, so unwrap that before discarding anything.
    const structured = extractStructuredErrorBody(data);
    const message =
      (typeof data?.detail === 'string' && data.detail) ||
      (typeof structured?.message === 'string' && structured.message) ||
      (typeof data?.message === 'string' && data.message) ||
      'Failed to start practice session.';
    const error = new Error(message);
    error.status = response.status;
    error.detail = data?.detail;
    error.body = data;
    if (structured) {
      error.code = structured.code;
      error.structured = structured;
    }
    throw error;
  }

  return data;
}

export async function postStartExam(sessionId) {
  return apiFetch(`/sessions/${sessionId}/start-exam`, { method: 'POST' });
}

export async function getExamQuestions(sessionId) {
  return apiFetch(`/sessions/${sessionId}/exam/questions`);
}

export async function postExamNavigate(sessionId, payload) {
  return apiFetch(`/sessions/${sessionId}/exam/navigate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function getExamQuestionState(sessionId, orderIndex) {
  return apiFetch(`/sessions/${sessionId}/exam/questions/${orderIndex}/state`);
}

export async function postEndExam(sessionId) {
  return apiFetch(`/sessions/${sessionId}/exam/end`, { method: 'POST' });
}

export async function getExamResults(sessionId) {
  return apiFetch(`/sessions/${sessionId}/exam/results`);
}

export async function getSessionSummary(sessionId) {
  return apiFetch(`/sessions/${sessionId}/summary`);
}

export async function postNextQuestion(sessionId, { mastery = false, createAttempt = true } = {}) {
  const path = mastery
    ? `/sessions/${sessionId}/next-question/mastery?create_attempt=${createAttempt ? 'true' : 'false'}`
    : `/sessions/${sessionId}/next-question?create_attempt=${createAttempt ? 'true' : 'false'}`;
  return apiFetch(path, { method: 'POST' });
}

export async function getMasteryInspect(sessionId) {
  return apiFetch(`/sessions/${sessionId}/mastery/inspect`);
}

export async function getMasteryInspectWithFallback(sessionId, { allowDebugFallback = false } = {}) {
  const response = await getMasteryInspect(sessionId);
  if (allowDebugFallback && response.status === 404) {
    return getMasteryDebugState(sessionId);
  }
  return response;
}

export async function getMasteryDebugState(sessionId) {
  return apiFetch(`/sessions/${sessionId}/debug/mastery/state`);
}

export async function getAttemptState(attemptId) {
  return apiFetch(`/attempts/${attemptId}/state`);
}

export async function postSubmitPart(attemptId, partLabel, formData) {
  return apiFetch(`/part-attempts/attempts/${attemptId}/part/${partLabel}/submit`, {
    method: 'POST',
    body: formData,
  });
}

export async function postMarkPartAttempt(partAttemptId, { forceCorrect = false } = {}) {
  const suffix = forceCorrect ? '?force_correct=true' : '';
  return apiFetch(`/part-attempts/${partAttemptId}/mark${suffix}`, { method: 'POST' });
}

export async function patchConfirmPartAttemptTranscription(partAttemptId, payload) {
  return apiFetch(`/part-attempts/${partAttemptId}/confirm`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function postSkipPartAttempt(partAttemptId, payload) {
  return apiFetch(`/part-attempts/${partAttemptId}/skip`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function postSkipPartByLabel(attemptId, partLabel, payload) {
  return apiFetch(`/part-attempts/attempts/${attemptId}/part/${partLabel}/skip`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function postFinalizeAttempt(attemptId) {
  return apiFetch(`/attempts/${attemptId}/finalize`, { method: 'POST' });
}

export async function postAbandonAttempt(attemptId) {
  return apiFetch(`/attempts/${attemptId}/abandon`, { method: 'POST' });
}

export async function postEndSession(sessionId) {
  return apiFetch(`/sessions/${sessionId}/end`, { method: 'POST' });
}

export async function postSubmitAppFeedback(payload) {
  return apiFetch('/feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function postOverridePartAttempt(partAttemptId, payload) {
  return apiFetch(`/part-attempts/${partAttemptId}/override`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function postConfirmPartAttemptEvidence(partAttemptId, payload = {}) {
  return apiFetch(`/part-attempts/${partAttemptId}/confirm-evidence`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload || {}),
  });
}

export async function getAttemptDraft(attemptId) {
  return apiFetch(`/attempts/${attemptId}/draft`);
}

export async function putAttemptDraft(attemptId, payload) {
  return apiFetch(`/attempts/${attemptId}/draft`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}
